import { useState, type FormEvent } from "react";
import { ArrowRight, Loader2 } from "lucide-react";

import { apiJson } from "@/lib/api";

type AccessGateProps = {
  onUnlocked: () => void;
};

export const AccessGate = ({ onUnlocked }: AccessGateProps) => {
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim() || pending) return;

    setPending(true);
    setError(null);
    try {
      await apiJson("/api/access", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      onUnlocked();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Invalid access code."
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="grid min-h-[70vh] place-items-center">
      <form onSubmit={submit} className="w-full max-w-sm border bg-card">
        <div className="flex items-center gap-3 border-b p-5">
          <img src="/assets/logo-mark.svg" alt="WE AS WEB" className="h-5 w-auto" />
          <span className="font-mono text-xs uppercase tracking-[1.2px] text-muted-foreground">
            Restricted access
          </span>
        </div>

        <div className="space-y-4 p-5">
          <label className="block font-mono text-[11px] uppercase tracking-[0.8px] text-subtle">
            Access code
            <input
              type="password"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoFocus
              className="mt-2 w-full border-0 border-b border-input bg-transparent py-2 font-mono text-base tracking-[2px] text-foreground outline-none transition-colors focus:border-foreground"
              placeholder="••••••"
            />
          </label>

          {error ? (
            <p className="border border-primary bg-primary/5 px-3 py-2 text-sm text-primary">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={pending || !code.trim()}
            className="flex w-full items-center justify-between bg-primary px-4 py-3 text-sm text-primary-foreground transition-colors hover:bg-primary-hover disabled:pointer-events-none disabled:opacity-50"
          >
            Enter workshop
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
